import React, { useMemo, useState } from 'react';
import { ChevronDown, Loader2, PlugZap, Plus, Trash2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Form } from '@/components/ui/form';
import { Sheet, SheetBody, SheetContent, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/hooks/useConfirm';
import { cn } from '@/lib/utils';
import {
    useCheckConnection,
    useDeleteIntegration,
    useToggleIntegration,
} from '@/api/integrations/use-integrations';
import type {
    ConnectionCheckResult,
    TenantIntegration,
} from '@/api/integrations/integrationService';
import { environmentOf, getChannel, type CatalogProvider } from './catalog';
import { DrawerHeading } from './DrawerHeading';
import { deriveChannelStatus, derivePaymentStatus, summarizePayments } from './integrationHelpers';
import { ProviderFields } from './ProviderFields';
import { useMetaLine } from './useMetaLine';
import { useProviderForm } from './useProviderForm';

interface PaymentsDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Every saved payment provider. */
    rows: TenantIntegration[];
    canEdit: boolean;
    canCheck: boolean;
}

/** The inline editor of one provider: Environment control, secrets, webhook URL, Remove / Check / Save. */
const PaymentEditor: React.FC<{
    provider: CatalogProvider;
    row?: TenantIntegration;
    canEdit: boolean;
    canCheck: boolean;
    /** Closes the editor (Cancel on a draft, or after Remove). */
    onDone: () => void;
}> = ({ provider, row, canEdit, canCheck, onDone }) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const { user } = useAuth();
    const deleteMutation = useDeleteIntegration();
    const check = useCheckConnection();
    const [result, setResult] = useState<ConnectionCheckResult | undefined>();

    const { form, isSaving, generalError, save } = useProviderForm({
        type: 'payment',
        provider,
        row,
    });
    const dirty = form.formState.isDirty;

    const tenantId = (user as { tenant_id?: string } | null)?.tenant_id;
    const webhookUrl = tenantId
        ? `${window.location.origin}/api/v1/integrations/webhooks/payment/${tenantId}`
        : undefined;

    const runCheck = async () => {
        if (!row) return;
        setResult(undefined);
        try {
            const outcome = await check.mutateAsync(row.id);
            setResult(outcome);
            if (outcome.supported !== false) {
                enqueueSnackbar(
                    outcome.message ||
                        (outcome.valid
                            ? t('integrations.verify.checkOk')
                            : t('integrations.verify.checkFailed')),
                    { variant: outcome.valid ? 'success' : 'error' }
                );
            }
        } catch (error: any) {
            setResult({
                valid: false,
                message: error?.response?.data?.message || t('integrations.verify.checkFailed'),
            });
        }
    };

    const remove = async () => {
        if (!row) return;
        const ok = await confirm({
            title: t('integrations.confirm.removeTitle', { name: provider.name }),
            description: t('integrations.confirm.removeDescription', {
                service: t('integrations.channels.payment.name'),
            }),
            confirmLabel: t('integrations.actions.remove'),
            variant: 'destructive',
        });
        if (!ok) return;
        try {
            await deleteMutation.mutateAsync(row.id);
            enqueueSnackbar(t('integrations.toasts.removed', { name: provider.name }), {
                variant: 'success',
            });
            onDone();
        } catch {
            enqueueSnackbar(t('integrations.toasts.removeFailed'), { variant: 'error' });
        }
    };

    return (
        <Form {...form}>
            <form
                noValidate
                onSubmit={event => {
                    event.preventDefault();
                    void save(row ? undefined : true);
                }}
                className="space-y-5 border-t border-border bg-muted/30 p-4"
            >
                {generalError && (
                    <Alert variant="destructive" role="alert">
                        <AlertDescription>{generalError}</AlertDescription>
                    </Alert>
                )}

                <ProviderFields
                    form={form}
                    provider={provider}
                    saved={row?.configuration}
                    disabled={!canEdit}
                    webhookUrl={webhookUrl}
                />

                {result && result.supported === false && (
                    <p className="text-xs text-muted-foreground">
                        {result.message || t('integrations.verify.unsupported')}
                    </p>
                )}
                {result && result.supported !== false && result.valid === false && (
                    <p className="flex items-start gap-1.5 text-xs text-destructive">
                        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {result.message || t('integrations.verify.checkFailed')}
                    </p>
                )}

                <div className="flex flex-wrap items-center gap-2">
                    {canEdit && row && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={remove}
                            disabled={deleteMutation.isPending}
                        >
                            <Trash2 className="h-4 w-4" />
                            {t('integrations.actions.remove')}
                        </Button>
                    )}
                    <div className="ms-auto flex flex-wrap items-center gap-2">
                        {canCheck && row && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={runCheck}
                                disabled={
                                    provider.checkSupported === false || dirty || check.isPending
                                }
                                title={
                                    provider.checkSupported === false
                                        ? t('integrations.verify.unsupported')
                                        : dirty
                                          ? t('integrations.verify.unsaved')
                                          : undefined
                                }
                            >
                                {check.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <PlugZap className="h-4 w-4" />
                                )}
                                {t('integrations.verify.checkAction')}
                            </Button>
                        )}
                        {!row && (
                            <Button type="button" variant="ghost" size="sm" onClick={onDone}>
                                {t('actions.cancel')}
                            </Button>
                        )}
                        {canEdit && provider.fields.length > 0 && (
                            <Button type="submit" size="sm" disabled={isSaving}>
                                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                                {t('actions.save')}
                            </Button>
                        )}
                    </div>
                </div>
                {canCheck && row && (
                    <p className="text-xs text-muted-foreground">
                        {provider.checkSupported === false
                            ? t('integrations.verify.unsupported')
                            : t('integrations.verify.checkHint')}
                    </p>
                )}
            </form>
        </Form>
    );
};

/**
 * Payments drawer: one row per provider (name, LIVE / SANDBOX, detail, Enabled switch),
 * Edit expands the form in place, "Add provider" picks from the providers not added yet.
 * Several providers may be on at once.
 */
export const PaymentsDrawer: React.FC<PaymentsDrawerProps> = ({
    open,
    onOpenChange,
    rows,
    canEdit,
    canCheck,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const describe = useMetaLine();
    const toggle = useToggleIntegration();
    const channel = getChannel('payment');
    const [editing, setEditing] = useState<string | null>(null);

    const providers = useMemo<CatalogProvider[]>(() => {
        const known = new Set(channel.providers.map(provider => provider.id));
        const unknown = rows
            .filter(row => !known.has(row.provider_name))
            .map<CatalogProvider>(row => ({
                id: row.provider_name,
                name: row.provider_name,
                description: 'integrations.providers.unknown.description',
                fields: [],
            }));
        return [...channel.providers, ...unknown];
    }, [channel, rows]);

    const rowByProvider = useMemo(() => {
        const map: Record<string, TenantIntegration | undefined> = {};
        for (const row of rows) map[row.provider_name] ??= row;
        return map;
    }, [rows]);

    const added = providers.filter(provider => rowByProvider[provider.id]);
    const available = providers.filter(provider => !rowByProvider[provider.id]);
    const draft =
        editing && !rowByProvider[editing] ? providers.find(p => p.id === editing) : undefined;
    const listed = draft ? [...added, draft] : added;
    const otherAvailable = available.filter(provider => provider.id !== editing);

    const summary = summarizePayments(rows);
    const status = derivePaymentStatus(rows);

    const flip = async (row: TenantIntegration, enabled: boolean) => {
        try {
            await toggle.mutateAsync({ id: row.id, enabled });
        } catch {
            enqueueSnackbar(t('integrations.toasts.toggleFailed'), { variant: 'error' });
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent closeLabel={t('integrations.actions.close')}>
                <DrawerHeading channel={channel} />

                <SheetBody className="space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3">
                        <div className="min-w-0 space-y-1">
                            <StatusBadge
                                kind="integration"
                                status={status.state}
                                label={t(`integrations.status.${status.state}`)}
                            />
                            <p className="text-xs text-muted-foreground">
                                {summary.live + summary.sandbox > 0
                                    ? t('integrations.payments.counts', {
                                          live: summary.live,
                                          sandbox: summary.sandbox,
                                      })
                                    : describe(status).text}
                            </p>
                        </div>
                        {canEdit && otherAvailable.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button type="button" size="sm">
                                        <Plus className="h-4 w-4" />
                                        {t('integrations.payments.addProvider')}
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {otherAvailable.map(provider => (
                                        <DropdownMenuItem
                                            key={provider.id}
                                            onSelect={() => setEditing(provider.id)}
                                        >
                                            {provider.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>

                    {listed.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                            {t('integrations.payments.empty')}
                        </p>
                    )}

                    <ul className="space-y-3">
                        {listed.map(provider => {
                            const row = rowByProvider[provider.id];
                            const isOpen = editing === provider.id;
                            const rowStatus = deriveChannelStatus(row ? [row] : []);
                            const meta = describe(rowStatus);
                            const env = row ? environmentOf(row.configuration) : undefined;
                            const detail = row
                                ? provider.detail?.(row.configuration ?? {})
                                : undefined;
                            return (
                                <li
                                    key={provider.id}
                                    className="overflow-hidden rounded-lg border border-border bg-card"
                                >
                                    <div className="flex flex-wrap items-center gap-3 p-3">
                                        <div className="min-w-0 flex-1 space-y-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-medium">
                                                    {provider.name}
                                                </span>
                                                {env && (
                                                    <StatusBadge
                                                        kind="integration"
                                                        status={env}
                                                        label={t(
                                                            `integrations.options.${env}`
                                                        ).toUpperCase()}
                                                    />
                                                )}
                                                {!row && (
                                                    <span className="text-xs text-muted-foreground">
                                                        {t('integrations.payments.new')}
                                                    </span>
                                                )}
                                            </div>
                                            {row && (
                                                <p
                                                    className={cn(
                                                        'truncate text-xs',
                                                        meta.tone === 'error'
                                                            ? 'text-destructive'
                                                            : 'text-muted-foreground'
                                                    )}
                                                >
                                                    {[detail, meta.text]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </p>
                                            )}
                                        </div>
                                        {row && (
                                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                                {t('integrations.drawer.enabled')}
                                                <Switch
                                                    checked={row.is_active}
                                                    onCheckedChange={enabled =>
                                                        void flip(row, enabled)
                                                    }
                                                    disabled={!canEdit || toggle.isPending}
                                                    aria-label={`${t('integrations.drawer.enabled')}: ${provider.name}`}
                                                />
                                            </label>
                                        )}
                                        {row && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                aria-expanded={isOpen}
                                                onClick={() =>
                                                    setEditing(isOpen ? null : provider.id)
                                                }
                                            >
                                                {canEdit
                                                    ? t('actions.edit')
                                                    : t('integrations.actions.view')}
                                            </Button>
                                        )}
                                    </div>
                                    {isOpen && (
                                        <PaymentEditor
                                            key={provider.id}
                                            provider={provider}
                                            row={row}
                                            canEdit={canEdit}
                                            canCheck={canCheck}
                                            onDone={() => setEditing(null)}
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {otherAvailable.length > 0 && (
                        <div className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                            {t('integrations.payments.available', {
                                names: otherAvailable.map(provider => provider.name).join(', '),
                            })}
                        </div>
                    )}
                </SheetBody>

                <SheetFooter>
                    <Button type="button" className="ms-auto" onClick={() => onOpenChange(false)}>
                        {t('integrations.actions.close')}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default PaymentsDrawer;
